<template>
  <div class="flex flex-col gap-4 pt-4">
    <span class="text-body-large">{{ $t('lumia.phase.reflexionIntro') }}</span>
    <div v-if="lessonFeedback?.running && lessonFeedback.teams.length">
      <BaseTextButton color="filled" size="S" icon="share" @click="openFeedbackLinkDialog">
        {{ $t('lumia.lesson.feedback.showLink') }}
      </BaseTextButton>
      <LessonFeedbackLinkDialog
        :is-open="isFeedbackLinkDialogOpen"
        :teams="lessonFeedback.teams"
        @close="closeFeedbackLinkDialog"
      />
    </div>
    <div v-else-if="lessonFeedback && !lessonFeedback.running && canStartFeedback">
      <BaseTextButton color="filled" size="S" icon="rate_review" @click="openStartFeedbackDialog">
        {{ $t('lumia.lesson.feedback.start') }}
      </BaseTextButton>
      <StartLessonFeedbackDialog
        :is-open="isStartFeedbackDialogOpen"
        mode="start"
        :teams="lessonFeedback.teams"
        :members="lessonFeedback.members"
        :is-submitting="isStartingFeedback"
        @close="closeStartFeedbackDialog"
        @start="onStartFeedback"
      />
    </div>
    <div v-if="reflexionQuestions.length > 0">
      <h4 class="text-title-medium mb-1">{{ $t('lumia.sections.reflexionQuestions') }}</h4>
      <ul class="flex flex-col gap-0.5">
        <li
          v-for="question in reflexionQuestions"
          :key="question.id"
          class="bg-surface-container-lowest rounded-extra-small first:rounded-t-medium last:rounded-b-medium px-3 py-1.5"
        >
          <span class="text-body-medium">{{ question.text }}</span>
        </li>
      </ul>
    </div>
    <div v-if="indicators.length > 0">
      <h4 class="text-title-medium mb-1">{{ $t('lumia.phase.formativeAssessment') }}</h4>
      <ul class="flex flex-col gap-0.5">
        <li
          v-for="indicator in indicators"
          :key="indicator.id"
          class="bg-surface-container-lowest rounded-extra-small first:rounded-t-medium last:rounded-b-medium px-2 py-1.5"
        >
          <span class="text-body-medium">{{ indicator.studentPhrasing }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { usePermission } from '@/core/composables/use-permission'
  import { Permission } from '@/core/models/user'
  import type { Member } from '@/core/models/member/member'
  import { useLesson } from '../composables/use-lesson'
  import type { LessonTeam, StartLessonFeedbackBody } from '../models/lesson'
  import type { Indikator } from '../models/lesson-impulse'
  import LessonFeedbackLinkDialog from './LessonFeedbackLinkDialog.vue'
  import StartLessonFeedbackDialog from './StartLessonFeedbackDialog.vue'

  interface Props {
    reflexionQuestions: ReadonlyArray<{ id: string; text: string }>
    indicators: ReadonlyArray<Indikator>
    /** Present only while the Lesson is running. */
    lessonFeedback?: {
      readonly running: boolean
      readonly teams: ReadonlyArray<LessonTeam>
      readonly members: ReadonlyArray<Member>
    }
  }

  defineProps<Props>()

  const { can } = usePermission()
  const { startFeedback } = useLesson()
  const canStartFeedback = computed(() => can(Permission.LESSON_START))
  const isStartingFeedback = ref(false)

  const {
    isOpen: isFeedbackLinkDialogOpen,
    openDialog: openFeedbackLinkDialog,
    closeDialog: closeFeedbackLinkDialog,
  } = useDialog()
  const {
    isOpen: isStartFeedbackDialogOpen,
    openDialog: openStartFeedbackDialog,
    closeDialog: closeStartFeedbackDialog,
  } = useDialog()

  const onStartFeedback = async (body: StartLessonFeedbackBody) => {
    isStartingFeedback.value = true
    const started = await startFeedback(body)
    isStartingFeedback.value = false
    if (started) {
      closeStartFeedbackDialog()
    }
  }
</script>
